import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { SPACE_PERMISSIONS_SETTINGS } from '@confluence/named-routes';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys';
import { useSpaceKey } from '@confluence/space-utils';
import { ReverseTrialLozengeExperiment } from '@confluence/trial-components';
import { ConfluenceEdition } from '@confluence/session-data';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment';
import { SPACE_PERMISSIONS_WEBITEM_SECTION_ID } from './useSpacePermissionsWebItems';
import type {
	SpaceSettingsWebItemSectionsInjector,
	WebItemSectionsWithLozenge_items,
} from './SpaceSettingsWebItemSectionsInjector';

export const SPACE_PUBLIC_LINKS_WEBITEM_ID = 'space-public-links';
const SPACE_PERMISSIONS_COMPLETE_KEY = 'confluence-frontend.';

const i18n = defineMessages({
	spacePermissionsWebItemPublicLinksLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.public-links',
		defaultMessage: 'Public links',
		description: 'Label for the web item link for public links',
	},
});

const addPublicLinksWebItem = (
	publicLinksWebItem: SpaceSettingsWebItemSectionsFragment_webItemSections_items,
	webItemSections: (SpaceSettingsWebItemSectionsFragment_webItemSections | null)[] | undefined,
) => {
	if (!publicLinksWebItem) {
		return webItemSections;
	}

	return webItemSections?.map((section) => {
		if (section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
			// XXX Don't mutate the input webItemSections! May be shared across invocations.
			const [firstItem, ...restItems] = section.items;

			return {
				...section,
				items: [firstItem, publicLinksWebItem, ...restItems],
			};
		}
		return section;
	});
};

export const usePublicLinksWebItem: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	const intl = useIntl();
	const spaceKey = useSpaceKey();

	if (!spaceKey) {
		return webItemSections;
	}

	const publicLinksWebItem: WebItemSectionsWithLozenge_items = {
		id: SPACE_PUBLIC_LINKS_WEBITEM_ID,
		label: intl.formatMessage(i18n.spacePermissionsWebItemPublicLinksLink),
		url: SPACE_PERMISSIONS_SETTINGS.toUrl({
			spaceKey,
			tab: SpacePermissionsTabKey.PublicLinks,
		}),
		completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + SPACE_PUBLIC_LINKS_WEBITEM_ID,
		icon: null,
		lozenge: (
			<ReverseTrialLozengeExperiment
				touchpointId={`confluence_space_settings_${SPACE_PUBLIC_LINKS_WEBITEM_ID}`}
				edition={ConfluenceEdition.STANDARD}
			/>
		),
	};

	return addPublicLinksWebItem(publicLinksWebItem, webItemSections);
};
